import polyfill from "../packages/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { trackEvent } from "../packages/excalidraw/analytics";
import { getDefaultAppState } from "../packages/excalidraw/appState";
import { ErrorDialog } from "../packages/excalidraw/components/ErrorDialog";
import { TopErrorBoundary } from "./components/TopErrorBoundary";
import {
  APP_NAME,
  EVENT,
  THEME,
  TITLE_TIMEOUT,
  VERSION_TIMEOUT,
} from "../packages/excalidraw/constants";
import { loadFromBlob } from "../packages/excalidraw/data/blob";
import type {
  ExcalidrawImageElement,
  FileId,
  FractionalIndex,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "../packages/excalidraw/element/types";
import { useCallbackRefState } from "../packages/excalidraw/hooks/useCallbackRefState";
import { t } from "../packages/excalidraw/i18n";
import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  StoreAction,
  reconcileElements,
  Sidebar,
} from "../packages/excalidraw";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  NormalizedZoomValue,
  BinaryFileData,
} from "../packages/excalidraw/types";
import type { ResolvablePromise } from "../packages/excalidraw/utils";
import {
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
} from "../packages/excalidraw/utils";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import type { CollabAPI } from "./collab/Collab";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import {
  exportToBackend,
  getCollaborationLinkData,
  isCollaborationLink,
  loadScene,
} from "./data";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";
import CustomStats from "./CustomStats";
import type { RestoredDataState } from "../packages/excalidraw/data/restore";
import { restore, restoreAppState } from "../packages/excalidraw/data/restore";
import { exportToExcalidrawPlus } from "./components/ExportToExcalidrawPlus";
import { updateStaleImageStatuses } from "./data/FileManager";
import { newElementWith } from "../packages/excalidraw/element/mutateElement";
import { isInitializedImageElement } from "../packages/excalidraw/element/typeChecks";
import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "../packages/excalidraw/data/library";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { AppFooter } from "./components/AppFooter";
import { Provider, useAtom, useAtomValue } from "jotai";
import { useAtomWithInitialValue } from "../packages/excalidraw/jotai";
import { appJotaiStore } from "./app-jotai";

import "./index.scss";
import type { ResolutionType } from "../packages/excalidraw/utility-types";
import { ShareableLinkDialog } from "../packages/excalidraw/components/ShareableLinkDialog";
import { openConfirmModal } from "../packages/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { OverwriteConfirmDialog } from "../packages/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import Trans from "../packages/excalidraw/components/Trans";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import type { RemoteExcalidrawElement } from "../packages/excalidraw/data/reconcile";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "../packages/excalidraw/components/CommandPalette/CommandPalette";
import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "../packages/excalidraw/components/icons";
import { appThemeAtom, useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";

// map

import type { LatLng } from "leaflet";
import L from "leaflet";
import leafletImage from "leaflet-image";
import "leaflet/dist/leaflet.css";
import type { Radians } from "../packages/math";
import {
  convertBlobToDataURL,
  generateUniqueId,
  getImageDimensions,
} from "./image-utils";
// import { hasBackground } from "../packages/excalidraw/scene";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

// map

const lat = 51.837110872763596;
const lng = -1.3514689559705033;

const mapProviders = {
  OpenStreetMap: {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution:
      '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
  Esri: {
    name: "OpenStreetMap",
    url: "http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution:
      ' <a href="http://www.esri.com/">Esri</a> i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
    maxNativeZoom: 19,
    maxZoom: 22,
  },
};

const defaultMapProvider = mapProviders.OpenStreetMap;

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

// Map
// ---------------------------------------------------------------------------

function removeAlpha(color: string) {
  // Handle hex with alpha (#RRGGBBAA)
  if (color.length === 9) {
    return color.slice(0, 7);
  }
  // Handle rgba format
  if (color.startsWith("rgba")) {
    const values = color.match(/[\d.]+/g);
    if (values && values.length >= 3) {
      return `rgb(${values[0]}, ${values[1]}, ${values[2]})`;
    }
  }
  // Return original color if no alpha channel detected
  return color;
}

function addAlpha(color: string, alpha = 1) {
  // Handle hex colors (#RRGGBB)
  if (color.startsWith("#")) {
    // Remove any existing alpha
    const baseColor = color.slice(0, 7);
    // Convert alpha to hex
    const alphaHex = Math.round(alpha * 255)
      .toString(16)
      .padStart(2, "0");
    return `${baseColor}${alphaHex}`;
  }

  // Handle rgb format
  if (color.startsWith("rgb")) {
    // Remove any existing alpha
    const values = color.match(/[\d.]+/g);
    if (values && values.length >= 3) {
      return `rgba(${values[0]}, ${values[1]}, ${values[2]}, ${alpha})`;
    }
  }

  // Return original color if format not recognized
  return color;
}

// First, create a promise-based version of leafletImage
const leafletImageAsync = (map: any): Promise<HTMLCanvasElement> => {
  return new Promise((resolve, reject) => {
    leafletImage(map, (err: any, canvas: any) => {
      if (err) {
        reject(err);
      } else {
        resolve(canvas);
      }
    });
  });
};

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");

  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );

  // add map lng and lat to the url #position
  const mapBackendMatch = window.location.hash.match(/^#map=(.*)$/);

  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: RestoredDataState & {
    scrollToContent?: boolean;
  } = await loadScene(null, null, localDataState);

  if (mapBackendMatch) {
    scene.appState.viewBackgroundColor = addAlpha(
      getDefaultAppState().viewBackgroundColor,
      0.3,
    );
  }

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        scene = await loadScene(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
          localDataState,
        );
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const [appTheme, setAppTheme] = useAtom(appThemeAtom);
  const { editorTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  // Sidebar
  // ---------------------------------------------------------------------------

  const [docked, setDocked] = useState(true);

  const mapRef = useRef<L.Map | null>(null);
  const [currentZoom, setCurrentZoom] = useState(1); // Default zoom level
  const [leafletZoom, setLeafletZoom] = useState(19);
  const [leafletCenter, setLeafletCenter] = useState<LatLng | null>(null);
  const [leafletProvider, setLeafletProvider] = useState(defaultMapProvider);
  const [backgroundColor, setBackgroundColor] = useState<string | null>(null);
  const lastScrollPosition = useRef({ x: 0, y: 0 });
  const lastSize = useRef<{ width: number; height: number } | null>(null);
  const scaleFactorRef = useRef(1);

  const updateScaleFactor = useCallback(() => {
    if (mapRef.current) {
      const mapScale = getMapScale(mapRef.current);
      scaleFactorRef.current =
        mapScale.pixelsPerMeter * window.devicePixelRatio;
    }
  }, []);

  const getMapScale = (map: L.Map) => {
    const zoom = map.getZoom();
    const center = map.getCenter();
    const lat = center.lat;
    const metersPerPixelAtZoom0 = 156543.03392;
    const metersPerPixel =
      (metersPerPixelAtZoom0 * Math.cos((lat * Math.PI) / 180)) /
      Math.pow(2, zoom);
    const devicePixelRatio = window.devicePixelRatio || 1;
    const pixelsPerMeter = 1 / metersPerPixel;

    return {
      pixelsPerMeter,
      metersPerPixel,
      devicePixelRatio,
    };
  };

  // Map
  // ----------------------------------------------------------------

  const handleZoomIn = useCallback(() => {
    setCurrentZoom((prevZoom) => {
      const zoomScale =
        mapRef.current?.getZoomScale(prevZoom + 1, prevZoom) || 1;
      return Math.max(zoomScale, 0);
    });
    setLeafletZoom((prevZoom) => {
      // const zoomScale = mapRef.current?.getZoomScale(prevZoom + 1, prevZoom);
      const newZoom = Math.min(prevZoom + 1, 22);
      if (mapRef.current) {
        mapRef.current.setZoom(newZoom);
      }
      return newZoom;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setCurrentZoom((prevZoom) => {
      const zoomScale =
        mapRef.current?.getZoomScale(prevZoom - 1, prevZoom) || 1;
      return Math.max(zoomScale, 0);
    });
    setLeafletZoom((prevZoom) => {
      const newZoom = Math.max(prevZoom - 1, 0);
      if (mapRef.current) {
        mapRef.current.setZoom(newZoom);
      }
      return newZoom;
    });
  }, []);

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  // Map
  // ----------------------------------------------------------------

  const _addImageToScene = useCallback(
    async (
      excalidrawAPI: ExcalidrawImperativeAPI,
      url: string | URL | Request,
    ) => {
      const response = await fetch(url);
      const blob = await response.blob();

      const mimeType: any = blob.type;
      const dataURL = await convertBlobToDataURL(blob);
      const imageDimensions = await getImageDimensions(blob);

      const fileId = generateUniqueId();

      const fileData: BinaryFileData = {
        mimeType,
        id: fileId as FileId,
        dataURL,
        created: Date.now(),
        lastRetrieved: Date.now(),
        ...imageDimensions,
      };

      const id = `image-${Date.now()}`;
      const versionNonce = Math.random();
      const seed = Math.floor(Math.random() * 1000);

      const imageElement: ExcalidrawImageElement = {
        index: "a0" as FractionalIndex,
        type: "image",
        version: 1,
        versionNonce,
        isDeleted: false,
        id,
        fileId: fileId as FileId,
        status: "saved", // Required for image elements
        width: imageDimensions.width,
        height: imageDimensions.height,
        x: -lastScrollPosition.current.x,
        y: -lastScrollPosition.current.y,
        angle: 0 as Radians,
        strokeColor: "transparent", // Images typically don't have stroke
        backgroundColor: "transparent",
        fillStyle: "hachure",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 1,
        opacity: 80,
        groupIds: [],
        frameId: null, // Required frame reference
        roundness: null, // Required roundness property
        boundElements: null, // Required bound elements array
        updated: Date.now(), // Required update timestamp
        link: null, // Required link property
        locked: true, // Required lock status
        seed,
        scale: [1, 1], // Scale factors for x and y dimensions
        crop: {
          // Crop configuration
          x: 0,
          y: 0,
          width: imageDimensions.width,
          height: imageDimensions.height,
          naturalWidth: imageDimensions.width,
          naturalHeight: imageDimensions.height,
        },
        customData: {
          type: "mapBackgroundImage",
          zIndex: -999999,
          isBackground: true,
        },
      };

      const allElements = excalidrawAPI?.getSceneElements() || [];

      // Update the scene with the new image element and the files array
      excalidrawAPI?.updateScene({
        elements: [imageElement, ...allElements], // Add the new image element to the scene
      });

      excalidrawAPI?.addFiles([fileData]);
    },
    [],
  );

  const addDataURLToScene = useCallback(
    async (
      excalidrawAPI: ExcalidrawImperativeAPI,
      dataURL: any,
      imageDimensions: { width: number; height: number },
    ) => {
      const fileId = generateUniqueId();

      const fileData: BinaryFileData = {
        mimeType: "image/png",
        id: fileId as FileId,
        dataURL,
        created: Date.now(),
        lastRetrieved: Date.now(),
        ...imageDimensions,
      };

      const id = `image-${Date.now()}`;
      const versionNonce = Math.random();
      const seed = Math.floor(Math.random() * 1000);

      const imageElement: ExcalidrawImageElement = {
        index: "a0" as FractionalIndex,
        type: "image",
        version: 1,
        versionNonce,
        isDeleted: false,
        id,
        fileId: fileId as FileId,
        status: "saved", // Required for image elements
        width: imageDimensions.width,
        height: imageDimensions.height,
        x: -lastScrollPosition.current.x,
        y: -lastScrollPosition.current.y,
        angle: 0 as Radians,
        strokeColor: "transparent", // Images typically don't have stroke
        backgroundColor: "transparent",
        fillStyle: "hachure",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 1,
        opacity: 80,
        groupIds: [],
        frameId: null, // Required frame reference
        roundness: null, // Required roundness property
        boundElements: null, // Required bound elements array
        updated: Date.now(), // Required update timestamp
        link: null, // Required link property
        locked: true, // Required lock status
        seed,
        scale: [1, 1], // Scale factors for x and y dimensions
        crop: {
          // Crop configuration
          x: 0,
          y: 0,
          width: imageDimensions.width,
          height: imageDimensions.height,
          naturalWidth: imageDimensions.width,
          naturalHeight: imageDimensions.height,
        },
        customData: {
          type: "mapBackgroundImage",
          zIndex: -999999,
          isBackground: true,
        },
      };

      const allElements = excalidrawAPI?.getSceneElements() || [];

      // Update the scene with the new image element and the files array
      excalidrawAPI?.updateScene({
        elements: [imageElement, ...allElements], // Add the new image element to the scene
      });

      excalidrawAPI?.addFiles([fileData]);
    },
    [],
  );

  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = L.map("map", {
        zoomControl: false,
        dragging: true,
        scrollWheelZoom: true,
        doubleClickZoom: false,
        boxZoom: false,
        keyboard: false,
        tap: false,
        touchZoom: false,
        zoom: leafletZoom,
      }).setView([lat, lng], leafletZoom);

      L.tileLayer(
        leafletProvider.url /* "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" */,
        {
          attribution:
            leafletProvider.attribution /* " OpenStreetMap contributors",*/,
          maxNativeZoom: leafletProvider.maxNativeZoom, //19
          maxZoom: leafletProvider.maxZoom, // 22
        },
      ).addTo(mapRef.current);

      const scaleControl = L.control
        .scale({
          position: "bottomleft",
          maxWidth: 200,
          metric: true,
          imperial: true,
          updateWhenIdle: false,
        })
        .addTo(mapRef.current);

      setLeafletCenter(mapRef.current.getCenter());

      // const topLeft = mapRef.current.getPixelBounds().min;
      // const bottomRight = mapRef.current.getPixelBounds().max;

      // if (topLeft) {
      //   console.log(topLeft);
      //   mapRef.current.setZoomAround(
      //     L.latLng(topLeft.x, topLeft.y),
      //     mapRef.current.getZoom(),
      //   );
      // }

      // L.tileLayer('http://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
      //   attribution: ' <a href="http://www.esri.com/">Esri</a> i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community',
      //   maxNativeZoom: 19,
      //   maxZoom: 22
      // }).addTo(mapRef.current);

      mapRef.current.on("moveend", updateScaleFactor);
      mapRef.current.on("zoomend", () => {
        if (mapRef.current) {
          console.log(mapRef.current.getZoom());
        }
      });
      mapRef.current.on("resize", () => {
        if (mapRef.current) {
          setLeafletCenter(mapRef.current.getCenter());
          console.log(mapRef.current.getCenter());
        }
      });
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.off("moveend", updateScaleFactor);
        mapRef.current.off("zoomend");
        mapRef.current.off("resize");
      }
    };
  }, [
    leafletProvider.attribution,
    leafletProvider.maxNativeZoom,
    leafletProvider.maxZoom,
    leafletProvider.url,
    leafletZoom,
    updateScaleFactor,
  ]);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (import.meta.env.DEV) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      const appState = excalidrawAPI.getAppState();

      if (lastSize.current !== null && mapRef.current !== null) {
        const dx = appState.scrollX - lastScrollPosition.current.x;
        const dy = appState.scrollY - lastScrollPosition.current.y;
        const dw = window.innerWidth - lastSize.current.width;
        const dh = window.innerHeight - lastSize.current.height;

        if (dx !== 0 || dy !== 0 || dw !== 0 || dh !== 0) {
          mapRef.current.panBy([-(dx - dw / 2), -(dy - dh / 2)], {
            animate: false,
          });
        }
      }

      lastScrollPosition.current = { x: appState.scrollX, y: appState.scrollY };
      lastSize.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              ...data.scene,
              ...restore(data.scene, null, null, { repairBindings: true }),
              storeAction: StoreAction.CAPTURE,
            });
          }
        });
      }
    };

    // const onResize = async (event: Event) => {
    //   setTimeout(() => {
    //     if (leafletCenter !== null) {
    //       mapRef.current?.setView(leafletCenter, leafletZoom, {
    //         animate: false,
    //       });
    //     }
    //   }, 0);
    // };

    // Example usage with the new background type
    // const addBackgroundImage = async (
    //   imageUrl: string,
    // ) => {
    //   // Get current elements
    //   const currentElements = excalidrawApi.getSceneElements();

    //   // Add new image element to the scene
    //   excalidrawApi.updateScene({
    //     elements: [...currentElements, imageElement],
    //   });

    //   // Manage layers to ensure proper ordering
    //   manageLayers(excalidrawApi);
    // };

    const handleKeyDown = async (event: KeyboardEvent) => {
      if (event.altKey) {
        if (event.key === "≠") {
          event.preventDefault();
          handleZoomIn();
        } else if (event.key === "–") {
          event.preventDefault();
          handleZoomOut();
        } else if (event.key === "µ") {
          if (mapRef.current) {
            try {
              const canvas = await leafletImageAsync(mapRef.current);
              const dimensions = mapRef.current.getSize();
              const imageDimensions = {
                width: dimensions.x,
                height: dimensions.y,
              };
              await addDataURLToScene(
                excalidrawAPI,
                canvas.toDataURL(),
                imageDimensions as any,
              );
            } catch (error) {
              console.error("Error capturing map:", error);
            }
          }
          // addImageToScene(excalidrawAPI, "/leaf.jpg");

          // // Example of how to use these functions together
          // const handleSceneUpdate = (excalidrawApi) => {
          //   const backgroundImages = getMapBackgroundImages(excalidrawApi);
          //   const otherElements = getNonBackgroundElements(excalidrawApi);

          //   console.log("Background Images:", backgroundImages);
          //   console.log("Other Elements:", otherElements);

          //   manageLayers(excalidrawApi);
          // };
        }
      }
    };

    const titleTimeout = setTimeout(
      () => (document.title = APP_NAME),
      TITLE_TIMEOUT,
    );

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            storeAction: StoreAction.UPDATE,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);

    // window.addEventListener(EVENT.RESIZE, onResize, false);
    window.addEventListener(EVENT.KEYDOWN, handleKeyDown, false);

    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
      // window.removeEventListener(EVENT.RESIZE, onResize, false);
      window.removeEventListener(EVENT.KEYDOWN, handleKeyDown, false);
      clearTimeout(titleTimeout);
    };
  }, [
    isCollabDisabled,
    collabAPI,
    excalidrawAPI,
    setLangCode,
    handleZoomIn,
    handleZoomOut,
    addDataURLToScene,
  ]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        preventUnload(event);
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  // Map
  // ----------------------------------------------------------------

  useEffect(() => {
    if (backgroundColor !== null) {
      excalidrawAPI?.updateScene({
        appState: {
          viewBackgroundColor: backgroundColor, // example color with alpha
          zoom: {
            value: currentZoom as NormalizedZoomValue,
          },
        },
      });
    }
  }, [backgroundColor, currentZoom, excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // Map
    // --------------------------------------------
    if (!lastScrollPosition.current || !mapRef.current) {
      lastScrollPosition.current = { x: appState.scrollX, y: appState.scrollY };
      return;
    }

    if (!lastSize.current) {
      lastSize.current = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      return;
    }

    // update zoom level
    const newZoom = appState.zoom.value;

    if (backgroundColor === null) {
      setBackgroundColor(addAlpha(appState.viewBackgroundColor, 0.3));
    }

    if (newZoom !== currentZoom) {
      if (newZoom === 1) {
        setBackgroundColor(addAlpha(appState.viewBackgroundColor, 0.3));
      } else {
        setBackgroundColor(removeAlpha(appState.viewBackgroundColor));
      }
      setCurrentZoom(newZoom);
    }

    // const mapScale = getMapScale(mapRef.current);

    const dx = appState.scrollX - lastScrollPosition.current.x;
    const dy = appState.scrollY - lastScrollPosition.current.y;
    const dw = window.innerWidth - lastSize.current.width;
    const dh = window.innerHeight - lastSize.current.height;

    if (dx !== 0 || dy !== 0 || dw !== 0 || dh !== 0) {
      mapRef.current.panBy([-(dx - dw / 2), -(dy - dh / 2)], {
        animate: false,
      });
    }

    lastScrollPosition.current = { x: appState.scrollX, y: appState.scrollY };
    lastSize.current = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              storeAction: StoreAction.UPDATE,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        window.devicePixelRatio,
        () => forceRefresh((prev) => !prev),
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  const openMapSidebar = () => {
    excalidrawAPI?.toggleSidebar({
      name: "map",
      tab: "tool", // Optional: specify a tab to open
    });
  };

  return (
    <div className="app-container">
      <div id="map"></div>
      <div
        style={{ height: "100%" }}
        className={clsx("excalidraw-app", {
          "is-collaborating": isCollaborating,
        })}
      >
        <Excalidraw
          excalidrawAPI={excalidrawRefCallback}
          onChange={onChange}
          initialData={initialStatePromiseRef.current.promise}
          isCollaborating={isCollaborating}
          onPointerUpdate={collabAPI?.onPointerUpdate}
          UIOptions={{
            canvasActions: {
              toggleTheme: true,
              export: {
                onExportToBackend,
                renderCustomUI: (elements, appState, files) => {
                  return <>Hello</>;
                },
              },
            },
          }}
          langCode={langCode}
          renderCustomStats={renderCustomStats}
          detectScroll={false}
          handleKeyboardGlobally={true}
          autoFocus={true}
          theme={editorTheme}
          renderTopRightUI={(isMobile) => {
            if (isMobile || !collabAPI || isCollabDisabled) {
              return null;
            }
            return (
              <div className="top-right-ui">
                {collabError.message && (
                  <CollabError collabError={collabError} />
                )}
                <LiveCollaborationTrigger
                  isCollaborating={isCollaborating}
                  onSelect={() =>
                    setShareDialogState({ isOpen: true, type: "share" })
                  }
                />
                <Sidebar.Trigger
                  name="map"
                  tab="tool"
                  style={{
                    marginLeft: "0.5rem",
                    background: "#11528f",
                    color: "white",
                    width: "fit-content",
                  }}
                >
                  Map
                </Sidebar.Trigger>
              </div>
            );
          }}
        >
          <AppMainMenu
            onCollabDialogOpen={onCollabDialogOpen}
            isCollaborating={isCollaborating}
            isCollabEnabled={!isCollabDisabled}
            theme={appTheme}
            setTheme={(theme) => setAppTheme(theme)}
            refresh={() => forceRefresh((prev) => !prev)}
          />
          <AppWelcomeScreen
            onCollabDialogOpen={onCollabDialogOpen}
            isCollabEnabled={!isCollabDisabled}
          />
          <OverwriteConfirmDialog>
            <OverwriteConfirmDialog.Actions.ExportToImage />
            <OverwriteConfirmDialog.Actions.SaveToDisk />
            {/* {excalidrawAPI && (
              <OverwriteConfirmDialog.Action
                title={t("overwriteConfirm.action.excalidrawPlus.title")}
                actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
                onClick={() => {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }}
              >
                {t("overwriteConfirm.action.excalidrawPlus.description")}
              </OverwriteConfirmDialog.Action>
            )} */}
          </OverwriteConfirmDialog>
          <AppFooter onChange={() => excalidrawAPI?.refresh()} />
          {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

          <TTDDialogTrigger />
          {isCollaborating && isOffline && (
            <div className="collab-offline-warning">
              {t("alerts.collabOfflineWarning")}
            </div>
          )}
          {latestShareableLink && (
            <ShareableLinkDialog
              link={latestShareableLink}
              onCloseRequest={() => setLatestShareableLink(null)}
              setErrorMessage={setErrorMessage}
            />
          )}
          {excalidrawAPI && !isCollabDisabled && (
            <Collab excalidrawAPI={excalidrawAPI} />
          )}

          <ShareDialog
            collabAPI={collabAPI}
            onExportToBackend={async () => {
              if (excalidrawAPI) {
                try {
                  await onExportToBackend(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                  );
                } catch (error: any) {
                  setErrorMessage(error.message);
                }
              }
            }}
          />

          {errorMessage && (
            <ErrorDialog onClose={() => setErrorMessage("")}>
              {errorMessage}
            </ErrorDialog>
          )}

          <CommandPalette
            customCommandPaletteItems={[
              {
                label: t("labels.liveCollaboration"),
                category: DEFAULT_CATEGORIES.app,
                keywords: [
                  "team",
                  "multiplayer",
                  "share",
                  "public",
                  "session",
                  "invite",
                ],
                icon: usersIcon,
                perform: () => {
                  setShareDialogState({
                    isOpen: true,
                    type: "collaborationOnly",
                  });
                },
              },
              {
                label: t("roomDialog.button_stopSession"),
                category: DEFAULT_CATEGORIES.app,
                predicate: () => !!collabAPI?.isCollaborating(),
                keywords: [
                  "stop",
                  "session",
                  "end",
                  "leave",
                  "close",
                  "exit",
                  "collaboration",
                ],
                perform: () => {
                  if (collabAPI) {
                    collabAPI.stopCollaboration();
                    if (!collabAPI.isCollaborating()) {
                      setShareDialogState({ isOpen: false });
                    }
                  }
                },
              },
              {
                label: t("labels.share"),
                category: DEFAULT_CATEGORIES.app,
                predicate: true,
                icon: share,
                keywords: [
                  "link",
                  "shareable",
                  "readonly",
                  "export",
                  "publish",
                  "snapshot",
                  "url",
                  "collaborate",
                  "invite",
                ],
                perform: async () => {
                  setShareDialogState({ isOpen: true, type: "share" });
                },
              },
              {
                label: "GitHub",
                icon: GithubIcon,
                category: DEFAULT_CATEGORIES.links,
                predicate: true,
                keywords: [
                  "issues",
                  "bugs",
                  "requests",
                  "report",
                  "features",
                  "social",
                  "community",
                ],
                perform: () => {
                  window.open(
                    "https://github.com/excalidraw/excalidraw",
                    "_blank",
                    "noopener noreferrer",
                  );
                },
              },
              {
                label: t("labels.followUs"),
                icon: XBrandIcon,
                category: DEFAULT_CATEGORIES.links,
                predicate: true,
                keywords: ["twitter", "contact", "social", "community"],
                perform: () => {
                  window.open(
                    "https://x.com/excalidraw",
                    "_blank",
                    "noopener noreferrer",
                  );
                },
              },
              {
                label: t("labels.discordChat"),
                category: DEFAULT_CATEGORIES.links,
                predicate: true,
                icon: DiscordIcon,
                keywords: [
                  "chat",
                  "talk",
                  "contact",
                  "bugs",
                  "requests",
                  "report",
                  "feedback",
                  "suggestions",
                  "social",
                  "community",
                ],
                perform: () => {
                  window.open(
                    "https://discord.gg/UexuTaE",
                    "_blank",
                    "noopener noreferrer",
                  );
                },
              },
              {
                label: "YouTube",
                icon: youtubeIcon,
                category: DEFAULT_CATEGORIES.links,
                predicate: true,
                keywords: [
                  "features",
                  "tutorials",
                  "howto",
                  "help",
                  "community",
                ],
                perform: () => {
                  window.open(
                    "https://youtube.com/@excalidraw",
                    "_blank",
                    "noopener noreferrer",
                  );
                },
              },
              ...(isExcalidrawPlusSignedUser
                ? [
                    {
                      ...ExcalidrawPlusAppCommand,
                      label: "Sign in / Go to Excalidraw+",
                    },
                  ]
                : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

              {
                label: t("overwriteConfirm.action.excalidrawPlus.button"),
                category: DEFAULT_CATEGORIES.export,
                icon: exportToPlus,
                predicate: true,
                keywords: ["plus", "export", "save", "backup"],
                perform: () => {
                  if (excalidrawAPI) {
                    exportToExcalidrawPlus(
                      excalidrawAPI.getSceneElements(),
                      excalidrawAPI.getAppState(),
                      excalidrawAPI.getFiles(),
                      excalidrawAPI.getName(),
                    );
                  }
                },
              },
              {
                ...CommandPalette.defaultItems.toggleTheme,
                perform: () => {
                  setAppTheme(
                    editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                  );
                },
              },
              {
                label: t("labels.installPWA"),
                category: DEFAULT_CATEGORIES.app,
                predicate: () => !!pwaEvent,
                perform: () => {
                  if (pwaEvent) {
                    pwaEvent.prompt();
                    pwaEvent.userChoice.then(() => {
                      // event cannot be reused, but we'll hopefully
                      // grab new one as the event should be fired again
                      pwaEvent = null;
                    });
                  }
                },
              },
            ]}
          />
          {isVisualDebuggerEnabled() && excalidrawAPI && (
            <DebugCanvas
              appState={excalidrawAPI.getAppState()}
              scale={window.devicePixelRatio}
              ref={debugCanvasRef}
            />
          )}

          <Sidebar name="map" docked={docked} onDock={setDocked}>
            <Sidebar.Header>
              <div>Map</div>
            </Sidebar.Header>

            <Sidebar.Tabs>
              <Sidebar.TabTriggers>
                <Sidebar.TabTrigger tab="tools">Tools</Sidebar.TabTrigger>
                <Sidebar.TabTrigger tab="Metrics">Metrics</Sidebar.TabTrigger>
                <Sidebar.TabTrigger tab="Data">Data</Sidebar.TabTrigger>
              </Sidebar.TabTriggers>

              <Sidebar.Tab tab="tools">Tools</Sidebar.Tab>
              <Sidebar.Tab tab="Metrics">Metrics data</Sidebar.Tab>
              <Sidebar.Tab tab="Data">Data</Sidebar.Tab>
            </Sidebar.Tabs>
          </Sidebar>
        </Excalidraw>
      </div>
    </div>
  );
};

const ExcalidrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider unstable_createStore={() => appJotaiStore}>
        <ExcalidrawWrapper />
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
